import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded'
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded'
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import {
  AUTH_NEW_PASSWORD,
  useAuth
} from '../../auth/AuthContext'
import {
  confirmPasswordReset,
  confirmSignUp,
  requestPasswordReset,
  resendSignUpCode,
  signUp
} from '../../auth/cognito'
import { CONTACT_PATH, SIGN_UP_TERMS_NEEDED, TERMS_PATH } from '../../constants/legal'
import { rememberSignUpAcceptance } from '../../service/terms'

const MODE_SIGN_IN = 'signIn'
const MODE_SIGN_UP = 'signUp'
const MODE_CONFIRM_SIGN_UP = 'confirmSignUp'
const MODE_FORGOT = 'forgot'
const MODE_RESET = 'reset'
const MODE_NEW_PASSWORD = 'newPassword'

const Feature = ({ icon, title, children }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Box sx={{ color: 'primary.light', pt: 0.25 }}>{icon}</Box>
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{children}</Typography>
    </Box>
  </Stack>
)

Feature.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
}

const AuthCardHeader = ({ title, subtitle }) => (
  <Stack spacing={1} alignItems="center" sx={{ mb: 3 }}>
    <VideocamRoundedIcon color="primary" sx={{ fontSize: 42 }} />
    <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '.08em' }}>
      HIGHLIGHTS
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" textAlign="center">
      {subtitle}
    </Typography>
  </Stack>
)

AuthCardHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired
}

const errorText = error => {
  if (!error) return null
  if (error.code === 'NotAuthorizedException') return 'Incorrect username or password.'
  if (error.code === 'UserNotFoundException') return 'No account was found for that username.'
  if (error.code === 'CodeMismatchException') return 'The verification code is incorrect.'
  if (error.code === 'ExpiredCodeException') return 'That verification code has expired.'
  if (error.code === 'LimitExceededException') return 'Too many attempts. Please wait and try again.'
  return error.message || String(error)
}

export default function LoginScreen () {
  const { signIn, completeNewPassword, status } = useAuth()
  const [mode, setMode] = useState(MODE_SIGN_IN)
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [info, setInfo] = useState(null)

  const resetMessages = () => {
    setError(null)
    setInfo(null)
  }

  const handleSignIn = async event => {
    event.preventDefault()
    resetMessages()
    setBusy(true)
    try {
      const result = await signIn(username.trim(), password)
      if (result.type === AUTH_NEW_PASSWORD) {
        setPassword('')
        setMode(MODE_NEW_PASSWORD)
      }
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const handleSignUp = async event => {
    event.preventDefault()
    resetMessages()
    setBusy(true)
    try {
      const result = await signUp(username.trim(), email.trim(), password)
      // Recorded by the API at this account's first sign-in on this device (TermsGate).
      rememberSignUpAcceptance(result?.username || username.trim())
      if (result.userConfirmed) {
        setInfo('Account created. Sign in with your new account.')
        setMode(MODE_SIGN_IN)
      } else {
        setInfo('A verification code was sent to your email address.')
        setMode(MODE_CONFIRM_SIGN_UP)
      }
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const handleConfirmSignUp = async event => {
    event.preventDefault()
    resetMessages()
    setBusy(true)
    try {
      await confirmSignUp(username.trim(), code.trim())
      setCode('')
      setInfo('Account verified. Sign in to continue.')
      setMode(MODE_SIGN_IN)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const handleResendSignUpCode = async () => {
    resetMessages()
    setBusy(true)
    try {
      await resendSignUpCode(username.trim())
      setInfo('A new verification code was sent to your email address.')
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const handleForgotPassword = async event => {
    event.preventDefault()
    resetMessages()
    if (!username.trim()) {
      setError(new Error('Enter your username first.'))
      return
    }

    setBusy(true)
    try {
      await requestPasswordReset(username.trim())
      setInfo('A verification code was sent to the email address on your account.')
      setMode(MODE_RESET)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const handleResetPassword = async event => {
    event.preventDefault()
    resetMessages()
    setBusy(true)
    try {
      await confirmPasswordReset(username.trim(), code.trim(), newPassword)
      setCode('')
      setNewPassword('')
      setPassword('')
      setInfo('Password updated. Sign in with your new password.')
      setMode(MODE_SIGN_IN)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const handleNewPassword = async event => {
    event.preventDefault()
    resetMessages()
    setBusy(true)
    try {
      await completeNewPassword(newPassword)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const renderForm = () => {
    if (mode === MODE_CONFIRM_SIGN_UP) {
      return (
        <Box component="form" onSubmit={handleConfirmSignUp}>
          <AuthCardHeader
            title="Verify account"
            subtitle="Enter the verification code sent to your email address."
          />
          <Stack spacing={2.25}>
            <TextField
              label="Username"
              value={username}
              disabled
              fullWidth
            />
            <TextField
              autoFocus
              label="Verification code"
              value={code}
              onChange={event => setCode(event.target.value)}
              autoComplete="one-time-code"
              fullWidth
              required
            />
            <Button type="submit" variant="contained" size="large" disabled={busy}>
              {busy ? 'Verifying...' : 'Verify account'}
            </Button>
            <Button
              type="button"
              color="inherit"
              onClick={handleResendSignUpCode}
              disabled={busy}
            >
              Resend verification code
            </Button>
            <Button
              type="button"
              color="inherit"
              onClick={() => {
                resetMessages()
                setMode(MODE_SIGN_IN)
              }}
            >
              Back to sign in
            </Button>
          </Stack>
        </Box>
      )
    }

    if (mode === MODE_SIGN_UP) {
      return (
        <Box component="form" onSubmit={handleSignUp}>
          <AuthCardHeader
            title="Create account"
            subtitle="Create a Highlights account, verify your email, then start uploading."
          />
          <Stack spacing={2.25}>
            <TextField
              autoFocus
              label="Username"
              value={username}
              onChange={event => setUsername(event.target.value)}
              autoComplete="username"
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={event => setEmail(event.target.value)}
              autoComplete="email"
              fullWidth
              required
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              autoComplete="new-password"
              fullWidth
              required
            />
            <FormControlLabel
              required
              control={<Checkbox checked={agreedToTerms} onChange={event => setAgreedToTerms(event.target.checked)} />}
              label={(
                <Typography variant="body2">
                  I agree to the{' '}
                  <Link component={RouterLink} to={TERMS_PATH} target="_blank" rel="noopener">Terms of Use</Link>.
                </Typography>
              )}
            />
            {agreedToTerms ? null : <Typography variant="caption" color="text.secondary">{SIGN_UP_TERMS_NEEDED}</Typography>}
            <Button type="submit" variant="contained" size="large" disabled={busy || !agreedToTerms}>
              {busy ? 'Creating account...' : 'Create account'}
            </Button>
            <Button
              type="button"
              color="inherit"
              onClick={() => {
                resetMessages()
                setMode(MODE_SIGN_IN)
              }}
            >
              Back to sign in
            </Button>
          </Stack>
        </Box>
      )
    }

    if (mode === MODE_RESET) {
      return (
        <Box component="form" onSubmit={handleResetPassword}>
          <AuthCardHeader
            title="Reset password"
            subtitle="Enter the verification code and choose a new password."
          />
          <Stack spacing={2.25}>
            <TextField
              label="Username"
              value={username}
              disabled
              fullWidth
            />
            <TextField
              autoFocus
              label="Verification code"
              value={code}
              onChange={event => setCode(event.target.value)}
              autoComplete="one-time-code"
              fullWidth
              required
            />
            <TextField
              label="New password"
              type="password"
              value={newPassword}
              onChange={event => setNewPassword(event.target.value)}
              autoComplete="new-password"
              fullWidth
              required
            />
            <Button type="submit" variant="contained" size="large" disabled={busy}>
              {busy ? 'Updating...' : 'Update password'}
            </Button>
            <Button
              type="button"
              color="inherit"
              onClick={() => {
                resetMessages()
                setMode(MODE_SIGN_IN)
              }}
            >
              Back to sign in
            </Button>
          </Stack>
        </Box>
      )
    }

    if (mode === MODE_NEW_PASSWORD || status === AUTH_NEW_PASSWORD) {
      return (
        <Box component="form" onSubmit={handleNewPassword}>
          <AuthCardHeader
            title="Choose a new password"
            subtitle="Your account requires a new password before continuing."
          />
          <Stack spacing={2.25}>
            <TextField
              autoFocus
              label="New password"
              type="password"
              value={newPassword}
              onChange={event => setNewPassword(event.target.value)}
              autoComplete="new-password"
              fullWidth
              required
            />
            <Button type="submit" variant="contained" size="large" disabled={busy}>
              {busy ? 'Updating...' : 'Set new password'}
            </Button>
          </Stack>
        </Box>
      )
    }

    if (mode === MODE_FORGOT) {
      return (
        <Box component="form" onSubmit={handleForgotPassword}>
          <AuthCardHeader
            title="Forgot password"
            subtitle="Enter your username and we’ll send a verification code to your account email."
          />
          <Stack spacing={2.25}>
            <TextField
              autoFocus
              label="Username"
              value={username}
              onChange={event => setUsername(event.target.value)}
              autoComplete="username"
              fullWidth
              required
            />
            <Button type="submit" variant="contained" size="large" disabled={busy}>
              {busy ? 'Sending...' : 'Send verification code'}
            </Button>
            <Button
              type="button"
              color="inherit"
              onClick={() => {
                resetMessages()
                setMode(MODE_SIGN_IN)
              }}
            >
              Back to sign in
            </Button>
          </Stack>
        </Box>
      )
    }

    return (
      <Box component="form" onSubmit={handleSignIn}>
        <AuthCardHeader
          title="Sign in"
          subtitle="Use your Highlights account to access your private video library."
        />
        <Stack spacing={2.25}>
          <TextField
            autoFocus
            label="Username"
            value={username}
            onChange={event => setUsername(event.target.value)}
            autoComplete="username"
            fullWidth
            required
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={event => setPassword(event.target.value)}
            autoComplete="current-password"
            fullWidth
            required
          />
          <Button type="submit" variant="contained" size="large" disabled={busy}>
            {busy ? 'Signing in...' : 'Sign in'}
          </Button>
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => {
              resetMessages()
              setMode(MODE_SIGN_UP)
            }}
            sx={{ alignSelf: 'center' }}
          >
            Create an account
          </Link>
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => {
              resetMessages()
              setMode(MODE_FORGOT)
            }}
            sx={{ alignSelf: 'center' }}
          >
            Forgot your password?
          </Link>
        </Stack>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: { xs: 3, md: 6 },
        background: 'radial-gradient(circle at 15% 20%, #123047 0%, #0d1720 38%, #070b0f 72%)'
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(320px, 1fr) minmax(420px, 520px)' },
            overflow: 'hidden',
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 28px 80px rgba(0,0,0,.45)',
            backgroundColor: 'background.paper'
          }}
        >
          <Stack
            spacing={4}
            justifyContent="center"
            sx={{
              p: { xs: 4, md: 7 },
              display: { xs: 'none', md: 'flex' },
              background: 'linear-gradient(145deg, rgba(13,49,68,.95), rgba(12,24,34,.98))'
            }}
          >
            <Box>
              <Typography variant="overline" color="primary.light" sx={{ letterSpacing: '.2em' }}>
                YOUR VIDEO WORKSPACE
              </Typography>
              <Typography variant="h3" sx={{ mt: 1, fontWeight: 800, lineHeight: 1.08 }}>
                Review the game.
                <br />
                Keep what matters.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2, maxWidth: 480 }}>
                Organize full-game video into useful clips, searchable tags and player stats without losing the original context.
              </Typography>
            </Box>

            <Stack spacing={2.5}>
              <Feature icon={<PlayCircleOutlineRoundedIcon />} title="Review">
                Keep full-game video and jump directly to the moments you care about.
              </Feature>
              <Feature icon={<ContentCutRoundedIcon />} title="Clip">
                Create precise highlights with start and end times, tags and notes.
              </Feature>
              <Feature icon={<GroupsRoundedIcon />} title="Share">
                Organize access by family, team and group while keeping the library private.
              </Feature>
            </Stack>
          </Stack>

          <Box
            sx={{
              p: { xs: 3, sm: 5 },
              minHeight: { xs: 'auto', md: 590 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{errorText(error)}</Alert> : null}
            {info ? <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert> : null}
            {renderForm()}
            <Divider sx={{ my: 3 }} />
            <Typography variant="caption" color="text.secondary" textAlign="center">
              Private hockey video, highlights, tags and stats.
              {' · '}
              <Link component={RouterLink} to={TERMS_PATH} target="_blank" rel="noopener">Terms of Use</Link>
              {' · '}
              <Link component={RouterLink} to={CONTACT_PATH} target="_blank" rel="noopener">Contact</Link>
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  )
}
